<template>
    <v-form
        ref="form"
        :value="internalValid"
        :disable="anyLoading"
        @input="onValidationChange"
        @submit.prevent="$emit('submit')"
    >
        <v-row>
            <!-- Add button to add new report forms -->
            <v-col cols="12" class="mb-4">
                <v-btn
                    color="primary"
                    block
                    @click="emitAddForm"
                    :disabled="loading.stations"
                >
                    Add New Report
                </v-btn>
            </v-col>

            <!-- Loop through each form in forms array and render inputs -->
            <v-col cols="12" v-for="(form, index) in forms" :key="index">
                <v-card outlined>
                    <v-card-title>
                        Report #{{ index + 1 }}
                        <v-spacer></v-spacer>
                        <!-- Remove button for each form -->
                        <v-btn icon @click="emitRemoveForm(index)">
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12" md="12">
                                <v-autocomplete
                                    v-model="form.station"
                                    :loading="loading.stations"
                                    :items="stations"
                                    label="Station*"
                                    :item-text="stationItemText"
                                    return-object
                                    :rules="[rules.required]"
                                    required
                                ></v-autocomplete>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="form.reportedRunoutDateTime"
                                    label="Reported Runout Date Time*"
                                    type="datetime-local"
                                    :min="minDateTime"
                                    :max="maxDateTime"
                                    :rules="[rules.required]"
                                    required
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="form.reportedDeliveryDateTime"
                                    label="Reported Delivery Date Time"
                                    type="datetime-local"
                                    :min="minDateTime"
                                    :max="maxDateTime"
                                    :rules="[rules.required]"
                                    required
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="form.penalty"
                                    label="Penalty"
                                    type="number"
                                />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- File upload section -->
            <v-col cols="12" class="mb-4">
                <v-file-input
                    v-model="file"
                    label="Upload Excel file"
                    prepend-icon="mdi-file-excel"
                    accept=".xlsx, .xls"
                    @change="handleFileUpload"
                />
            </v-col>

            <v-col cols="12">
                <v-btn
                    color="primary"
                    block
                    type="submit"
                    :loading="loading.report"
                    :disabled="!internalValid"
                >
                    Run Analysis
                </v-btn>
            </v-col>
        </v-row>
    </v-form>
</template>

<script>
import * as XLSX from 'xlsx'; // Use XLSX library for Excel file handling

export default {
    name: 'InputForm',
    props: {
        forms: Array, // Receive forms as a prop
        stations: Array,
        loading: Object,
        anyLoading: Boolean,
        rules: Object,
        valid: Boolean,
        minDateTime: String,
        maxDateTime: String
    },
    data() {
        return {
            internalValid: this.valid,
            file: null // Store uploaded file
        };
    },
    watch: {
        valid(newVal) {
            this.internalValid = newVal;
        }
    },
    methods: {
        stationItemText(station) {
            let text = station.Name
                ? station.Name + ' - #' + station.StationNumber
                : station.StationNumber;
            return text;
        },
        onValidationChange(valid) {
            this.internalValid = valid;
            this.$emit('update:valid', valid);
        },
        // Emit event to add a form
        emitAddForm() {
            this.$emit('add-form');
        },
        // Emit event to remove a form by index
        emitRemoveForm(index) {
            this.$emit('remove-form', index);
        },
        // Handle file upload and parse Excel to add stations to forms
        handleFileUpload() {
            if (this.file) {
                const reader = new FileReader();
                reader.onload = e => {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                        header: 1
                    });

                    const stationMap = new Map(); // To track stations within the file

                    jsonData.forEach((row, index) => {
                        if (index > 0 && row[0]) {
                            // Skip header row and only process rows with data
                            let storeNumber = row[0].toString(); // Ensure store number is treated as a string
                            if (storeNumber.startsWith('0')) {
                                storeNumber = storeNumber.slice(1); // Remove leading 0
                            }

                            // Convert storeNumber to a number for comparison with StationNumber
                            const storeNumberAsNumber = parseInt(
                                storeNumber,
                                10
                            );

                            // Convert Excel serial numbers to JavaScript date objects
                            const outageTime = this.excelDateToJSDate(row[5]); // Assuming Outage Time is in the 6th column
                            const deliveryTime = this.excelDateToJSDate(row[6]); // Assuming Delivery Time is in the 7th column
                            let penalty = parseFloat(row[8]) || 0; // Assuming Penalty is in the 9th column, and convert to a number

                            // Check if this station has already been added within the file
                            if (stationMap.has(storeNumberAsNumber)) {
                                const existingForm = stationMap.get(
                                    storeNumberAsNumber
                                );

                                // Compare the dates and update with the oldest values
                                const existingRunoutDateTime = new Date(
                                    existingForm.reportedRunoutDateTime
                                );
                                const existingDeliveryDateTime = new Date(
                                    existingForm.reportedDeliveryDateTime
                                );
                                const newRunoutDateTime = new Date(outageTime);
                                const newDeliveryDateTime = new Date(
                                    deliveryTime
                                );

                                // Calculate the time difference between delivery times (in hours)
                                const timeDifference = Math.abs(
                                    (newDeliveryDateTime -
                                        existingDeliveryDateTime) /
                                        (1000 * 60 * 60)
                                ); // In hours

                                // Check if the delivery times are less than 4 hours apart
                                if (timeDifference < 4) {
                                    // Merge the records
                                    existingForm.reportedRunoutDateTime =
                                        newRunoutDateTime <
                                        existingRunoutDateTime
                                            ? outageTime
                                            : existingForm.reportedRunoutDateTime;

                                    existingForm.reportedDeliveryDateTime =
                                        newDeliveryDateTime <
                                        existingDeliveryDateTime
                                            ? deliveryTime
                                            : existingForm.reportedDeliveryDateTime;

                                    // Sum penalties
                                    existingForm.penalty += penalty;
                                } else {
                                    // Delivery times are more than 4 hours apart, treat as a separate report
                                    stationMap.set(
                                        storeNumberAsNumber + '-' + index,
                                        {
                                            station: this.stations.find(
                                                st =>
                                                    parseInt(
                                                        st.StationNumber,
                                                        10
                                                    ) === storeNumberAsNumber
                                            ) || {
                                                Name: storeNumber,
                                                StationNumber: storeNumberAsNumber
                                            },
                                            reportedRunoutDateTime: outageTime,
                                            reportedDeliveryDateTime: deliveryTime,
                                            penalty: penalty
                                        }
                                    );
                                }
                            } else {
                                // No existing entry in the current file, add the new form
                                stationMap.set(storeNumberAsNumber, {
                                    station: this.stations.find(
                                        st =>
                                            parseInt(st.StationNumber, 10) ===
                                            storeNumberAsNumber
                                    ) || {
                                        Name: storeNumber,
                                        StationNumber: storeNumberAsNumber
                                    },
                                    reportedRunoutDateTime: outageTime,
                                    reportedDeliveryDateTime: deliveryTime,
                                    penalty: penalty // Initialize penalty with this report's value
                                });
                            }
                        }
                    });

                    // Convert the Map to an array of forms
                    const newForms = Array.from(stationMap.values());

                    // Emit the new forms to the parent to add them to the existing forms
                    this.$emit('add-multiple-forms', newForms);
                };
                reader.readAsArrayBuffer(this.file);
            }
        },
        // Utility function to convert Excel date serial to JS date
        excelDateToJSDate(serial) {
            const utc_days = Math.floor(serial - 25569); // Excel date is offset by 25569 days
            const utc_value = utc_days * 86400; // 86400 seconds in a day
            const date_info = new Date(utc_value * 1000); // Convert to milliseconds
            const fractional_day = serial - Math.floor(serial) + 0.00001; // Account for possible precision errors
            const total_seconds = Math.floor(86400 * fractional_day);
            const seconds = total_seconds % 60;
            const minutes = Math.floor(total_seconds / 60) % 60;
            const hours = Math.floor(total_seconds / 3600);

            // Now adjust the date_info to the local timezone by adding the timezone offset
            const timezoneOffset = date_info.getTimezoneOffset() * 60 * 1000; // in milliseconds
            const localDate = new Date(date_info.getTime() + timezoneOffset);

            // Manually format the date as "yyyy-MM-ddThh:mm"
            const year = localDate.getFullYear();
            const month = String(localDate.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
            const day = String(localDate.getDate()).padStart(2, '0');
            const formattedDate = `${year}-${month}-${day}T${String(
                hours
            ).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

            return formattedDate;
        }
    }
};
</script>
